import { TableFooter } from '@fukuri-admins/features/Table/TableFooter';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { PlusIcon } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPushNotifications } from '../../../api/notifications';
import { useAuth } from '../../../components/auth/AuthProvider';
import { ActionButton } from '../../../components/common/ActionButton';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { PushNotificationTableContent } from '../../../components/pushNotification/TableContent';
import { Notification } from '../../../types/resources/Notification';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import {
  getCreatePushNotificationLink,
  getEditPushNotificationLink,
} from '../../../utils/getLinks';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexPushNotification = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterParams] = useSearchParams();
  const [notificationList, setNotificationList] = useState<Notification[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });
  const { showToast } = useToast();

  const notificationTableColumns: ActionTableColumnsType[] = [
    { label: '配信状況', key: 'status', sortable: false, filterable: false },
    { label: 'タイトル', key: 'title', sortable: true, filterable: true },
    { label: '本文', key: 'body', sortable: true, filterable: true },
    { label: 'URL', key: 'link_url', sortable: true, filterable: true },
    { label: '配信先', key: 'group', sortable: false, filterable: false },
    { label: '対象ユーザー数', key: 'target_user_count', sortable: true, filterable: true },
    { label: '配信日時', key: 'deliver_at', sortable: true, filterable: true },
    // { label: '設定', key: 'action', sortable: false, filterable: false },
  ];

  const fetchNotificationList = async (page?: number) => {
    try {
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getPushNotifications(signOut, {
        ...(pageNumber && { page: pageNumber.toString() }),
      });

      setTotalPage(Number(headers['total-pages']));
      setTotalCount(Number(headers['total-count']));
      setNotificationList(data.notifications);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    fetchNotificationList(page);
  };

  const handleClickEdit = (notificationId: number) => {
    navigate(getEditPushNotificationLink(notificationId));
  };

  const handleClickDelete = (_notificationId: number) => {};

  useEffect(() => {
    fetchNotificationList();
  }, []);

  return (
    <div className={styles.Root} data-testid='partner-admin-page'>
      <ContentsHeader
        title='プッシュ通知'
        actions={
          <ActionButton
            content='新規作成'
            type='dark'
            icon={<PlusIcon />}
            iconPosition='left'
            action={() => navigate(getCreatePushNotificationLink())}
          />
        }
      />
      <div className={styles.tableContainer}>
        {isLoading ? (
          <div className={styles.loading}>
            <CircularProgress size='1rem' color='inherit' />
          </div>
        ) : (
          <div className={styles.Root__Table}>
            <PushNotificationTableContent
              rows={notificationList}
              columns={notificationTableColumns}
              sortParams={sortParams}
              headerSort={column =>
                handleSorting(
                  column,
                  sortParams,
                  notificationList,
                  setSortParams,
                  setNotificationList
                )
              }
              onClickEdit={handleClickEdit}
              onClickDelete={handleClickDelete}
            />
            <TableFooter
              onPageChange={handlePageChange}
              totalCount={totalCount}
              totalPage={totalPage}
              itemCount={notificationList.length}
            />
          </div>
        )}
      </div>
    </div>
  );
};
